import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { applyTheme, getStoredTheme } from "../utils/theme";
import {
  ROLE_ADMIN,
  ROLE_ENVIRONMENTAL_DISTRICT_OFFICER,
  ROLE_REVIEWER,
  ROLE_VIEWER,
  normalizeRole,
} from "../constants/roles";

export default function Login({ setUser }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👁 NEW
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false); 

  const redirectByRole = (role) => {
    switch (normalizeRole(role)) {
      case ROLE_ADMIN:
        navigate("/dashboard", { replace: true });
        break;
      case ROLE_ENVIRONMENTAL_DISTRICT_OFFICER:
        navigate("/district-dashboard", { replace: true });
        break;
      case ROLE_REVIEWER:
        navigate("/reviewer-dashboard", { replace: true });
        break;
      case ROLE_VIEWER:
        navigate("/viewer-dashboard", { replace: true });
        break;
      default:
        navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    const autoLogin = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await api.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const currentUser = res.data?.user;
        if (!currentUser) return;

        localStorage.setItem("user", JSON.stringify(currentUser));
        setUser(currentUser);
        applyTheme(getStoredTheme());
        redirectByRole(currentUser.role);
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    };

    autoLogin();
  }, [navigate, setUser]);

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });

      const { token, user } = response.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
      applyTheme(getStoredTheme());

      switch (normalizeRole(user.role)) {
        case ROLE_ADMIN:
          navigate("/dashboard");
          break;
        case ROLE_ENVIRONMENTAL_DISTRICT_OFFICER:
          navigate("/district-dashboard");
          break;
        case ROLE_REVIEWER:
          navigate("/reviewer-dashboard");
          break;
        case ROLE_VIEWER:
          navigate("/viewer-dashboard");
          break;
        default:
          navigate("/");
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || "Unable to connect to server";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-green-600">
      <div className="bg-white p-6 rounded-xl w-[350px] shadow-lg">
        <h2 className="text-xl mb-4 font-semibold text-center">
          VertCorps ESMP Tracking System
        </h2>

        {/* Email */}
        <input
          type="email"
          className="border p-2 w-full mb-3 rounded"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {/* Password with Eye Icon */}
        <div className="relative mb-3">
          <input
            type={showPassword ? "text" : "password"}
            className="border p-2 w-full rounded pr-10"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              // Eye Off Icon
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20"
                viewBox="0 0 24 24"
                width="20"
                fill="currentColor"
              >
                <path d="M12 6a9.77 9.77 0 0 1 8.94 6 9.77 9.77 0 0 1-2.17 3.19l1.42 1.42A11.83 11.83 0 0 0 23 12 11.82 11.82 0 0 0 12 4a11.5 11.5 0 0 0-4.19.77l1.57 1.57A9.9 9.9 0 0 1 12 6zM2.1 3.51 3.51 2.1l18.39 18.39-1.41 1.41-3.2-3.2A11.73 11.73 0 0 1 12 20 11.82 11.82 0 0 1 1 12a11.73 11.73 0 0 1 3.31-4.79L2.1 3.51zM12 18a9.77 9.77 0 0 0 3.89-.8l-1.66-1.66A4 4 0 0 1 8.46 9.77L6.8 8.11A9.77 9.77 0 0 0 3.06 12 9.77 9.77 0 0 0 12 18z" />
              </svg>
            ) : (
              // Eye Icon
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20"
                viewBox="0 0 24 24"
                width="20"
                fill="currentColor"
              >
                <path d="M12 4C7 4 2.73 7.11 1 12c1.73 4.89 6 8 11 8s9.27-3.11 11-8c-1.73-4.89-6-8-11-8zm0 13a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
              </svg>
            )}
          </button>
        </div>

        {error && <p className="text-red-500 mb-3">{error}</p>}

        {/* ✅ Remember Me (ONLY ADDITION) */}
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
            className="mr-2 h-4 w-4"
          />
          <span className="text-sm text-gray-700">Remember Me</span>
        </div>

        {error && <p className="text-red-500 mb-3">{error}</p>}

        <button
          onClick={handleLogin}
          disabled={loading}
          className={`w-full p-2 mt-3 text-white rounded ${
            loading ? "bg-green-400 cursor-not-allowed" : "bg-green-700 hover:bg-green-800"
          }`}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </div>

      
    </div>
    
  );
}
